import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";

export const COACH_SUGGESTIONS_DRAWER_WIDTH = 440;
/**
 * Largeur du drawer "tableau financeur" : volontairement plus large que
 * le drawer suggestions pour accueillir la ligne par financeur (jauge +
 * carrousel de thèmes + actions).
 */
export const COACH_FUNDER_DRAWER_WIDTH = 720;
/**
 * Largeur du drawer "Smart Catalog brut" : plus large que les suggestions
 * pour accueillir la matrice de produits (durée / financement / sessions).
 */
export const COACH_SMART_CATALOG_DRAWER_WIDTH = 720;
const COACH_PANEL_WIDTH = 280;
const COACH_PANEL_RIGHT_OFFSET = 8;
const COACH_PANEL_GAP = 8;

const HEIGHT_STORAGE_KEY = "coach-suggestions-drawer-height";
const LEFT_STORAGE_KEY = "coach-suggestions-drawer-left";

const DEFAULT_HEIGHT = 360;
const MIN_HEIGHT = 140;
const MAX_HEIGHT_RATIO = 0.85;
const EDGE_MARGIN = 8;


function getMaxHeight(): number {
    return Math.floor(window.innerHeight * MAX_HEIGHT_RATIO);
}

function clampLeft(left: number, drawerWidth: number): number {
    const maxLeft = window.innerWidth - drawerWidth - EDGE_MARGIN;
    return Math.min(maxLeft, Math.max(EDGE_MARGIN, left));
}

function getDefaultLeft(drawerWidth: number): number {
    return clampLeft(window.innerWidth - COACH_PANEL_RIGHT_OFFSET - COACH_PANEL_WIDTH - COACH_PANEL_GAP - drawerWidth, drawerWidth);
}

function readStoredHeight(storageKey: string, defaultHeight: number, minHeight: number, maxHeight: number): number {
    try {
        const raw = localStorage.getItem(storageKey);
        if (raw) {
            const parsed = Number.parseInt(raw, 10);
            if (!Number.isNaN(parsed)) {
                return Math.min(maxHeight, Math.max(minHeight, parsed));
            }
        }
    } catch {
        // ignore storage errors
    }
    return Math.min(defaultHeight, maxHeight);
}

function readStoredLeft(storageKey: string, drawerWidth: number): number {
    try {
        const raw = localStorage.getItem(storageKey);
        if (raw) {
            const parsed = Number.parseInt(raw, 10);
            if (!Number.isNaN(parsed)) {
                return clampLeft(parsed, drawerWidth);
            }
        }
    } catch {
        // ignore storage errors
    }
    return getDefaultLeft(drawerWidth);
}

/**
 * Layout d'un drawer flottant déplaçable / redimensionnable.
 *
 * Retourne `isCollapsed` et `toggleCollapsed` pour que le chevron du
 * drawer reste synchronisé avec l'état du hook.
 */
export function useDrawerLayout(options?: { width?: number; heightStorageKey?: string; leftStorageKey?: string }) {
    const drawerWidth = options?.width ?? COACH_SUGGESTIONS_DRAWER_WIDTH;
    const heightStorageKey = options?.heightStorageKey ?? HEIGHT_STORAGE_KEY;
    const leftStorageKey = options?.leftStorageKey ?? LEFT_STORAGE_KEY;
    const minHeight = MIN_HEIGHT;

    const [height, setHeight] = useState(() => {
        const max = getMaxHeight();
        return readStoredHeight(heightStorageKey, DEFAULT_HEIGHT, minHeight, max);
    });

    const [left, setLeft] = useState(() => readStoredLeft(leftStorageKey, drawerWidth));
    const [isCollapsed, setIsCollapsed] = useState(false);
    /**
     * True pendant un drag (déplacement / redimensionnement). Sert à
     * désactiver la transition CSS de hauteur le temps du geste pour un
     * suivi instantané du curseur (sinon effet de "lag" caoutchouc).
     */
    const [isDragging, setIsDragging] = useState(false);

    /** Hauteur mémorisée avant collapse, pour la restaurer à l'expand. */
    const preCollapseHeightRef = useRef<number>(DEFAULT_HEIGHT);

    useEffect(() => {
        const handleResize = () => {
            const max = getMaxHeight();
            setHeight(current => Math.min(max, Math.max(minHeight, current)));
            setLeft(current => clampLeft(current, drawerWidth));
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [drawerWidth, minHeight]);

    /** Bascule collapse / expand depuis le chevron du drawer. */
    const toggleCollapsed = useCallback(() => {
        if (isCollapsed) {
            setIsCollapsed(false);
            setHeight(preCollapseHeightRef.current);
        } else {
            preCollapseHeightRef.current = height;
            setIsCollapsed(true);
        }
    }, [isCollapsed, height]);

    const onLayoutHandlePointerDown = useCallback(
        (event: ReactPointerEvent<HTMLElement>) => {
            event.preventDefault();
            const startX = event.clientX;
            const startY = event.clientY;
            const startLeft = left;
            const startHeight = height;
            const startCollapsed = isCollapsed;

            const cleanup = () => {
                window.removeEventListener("pointermove", onPointerMove);
                window.removeEventListener("pointerup", onPointerUp);
                document.body.style.userSelect = "";
                document.body.style.cursor = "";
                setIsDragging(false);
            };

            const onPointerMove = (moveEvent: PointerEvent) => {
                const newLeft = clampLeft(startLeft + (moveEvent.clientX - startX), drawerWidth);
                setLeft(newLeft);

                if (!startCollapsed) {
                    const maxHeight = getMaxHeight();
                    const deltaY = startY - moveEvent.clientY;
                    setHeight(Math.min(maxHeight, Math.max(minHeight, startHeight + deltaY)));
                }
            };

            const onPointerUp = (upEvent: PointerEvent) => {
                cleanup();

                const finalLeft = clampLeft(startLeft + (upEvent.clientX - startX), drawerWidth);
                setLeft(finalLeft);

                if (!startCollapsed) {
                    const maxHeight = getMaxHeight();
                    const finalHeight = Math.min(maxHeight, Math.max(minHeight, startHeight + (startY - upEvent.clientY)));
                    setHeight(finalHeight);
                    try {
                        localStorage.setItem(leftStorageKey, String(finalLeft));
                        localStorage.setItem(heightStorageKey, String(finalHeight));
                    } catch {
                        // ignore storage errors
                    }
                } else {
                    try {
                        localStorage.setItem(leftStorageKey, String(finalLeft));
                    } catch {
                        // ignore storage errors
                    }
                }
            };

            setIsDragging(true);
            document.body.style.userSelect = "none";
            document.body.style.cursor = "move";
            window.addEventListener("pointermove", onPointerMove);
            window.addEventListener("pointerup", onPointerUp);
        },
        [drawerWidth, height, left, isCollapsed, minHeight, heightStorageKey, leftStorageKey]
    );

    return { height, left, isCollapsed, isDragging, toggleCollapsed, onLayoutHandlePointerDown };
}
